import React, { useEffect, useMemo, useRef, useState } from 'react';
import { extname } from 'path';
import { useContextSelector } from 'use-context-selector';
import { PageVideo } from '@scormhero/shared';

import styles from './VideoHtml.module.scss';
import { UIContext } from '../../context/UI/UIContext';

type VideoHtmlProps = PageVideo & {
  baseURL: string;
  globalState: any;
};

function VideoHtml({
  resourceId,
  video,
  closedCaption,
  baseURL,
  settings = {},
  globalState,
}: VideoHtmlProps) {
  const {
    onPageCompleted,
    player,
    getTracking,
    patchTracking,
    getColorScheme,
  } = globalState;
  const [random, setRandom] = useState<number>();
  const [reloading, setReloading] = useState<boolean>(false);
  const { disableSeek } = settings;
  const videoRef = useRef<HTMLVideoElement>(null);
  const supposedCurrentTime = useRef<number>();
  const startBlinkNext = useContextSelector(UIContext, (c) => c.startBlinkNext);
  const [lastTimeAt, setLastTimeAt] = useState<{
    resourceId: string; // using resourceId here to make sure is lastTime from THAT resourceId
    time: number;
  }>({ resourceId: '', time: 0 });
  const [startAtTime, setStartAtTime] = useState<number>();
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const { mediaBackground } = getColorScheme();

  const filename: string = useMemo(
    () => (video.type === 'upload' ? video.name : ''),
    [video]
  );

  useEffect(() => {
    function onMount() {
      setStartAtTime(getTracking(resourceId)?.time);
      setIsMounted(true);
    }

    function onUmmount() {
      // reset in case next page is also a video
      setStartAtTime(0);
      setLastTimeAt({ resourceId: '', time: 0 });
      setIsMounted(false);
    }

    onMount();

    return () => onUmmount();
  }, []); // eslint-disable-line

  useEffect(() => {
    // force reload when data changes
    setRandom(Math.ceil(Math.random() * 1000));
  }, [filename]);

  useEffect(() => {
    setReloading(true);
    setTimeout(() => {
      setReloading(false);
    }, 100);
  }, [random]);

  useEffect(() => {
    if (
      isMounted &&
      lastTimeAt.time > 0 &&
      lastTimeAt.resourceId === resourceId // this check is required to avoid save time from previous video (if has in previous page)
    ) {
      patchTracking(resourceId, { time: lastTimeAt.time });
    }
  }, [lastTimeAt.time, lastTimeAt.resourceId, resourceId, isMounted]); // eslint-disable-line

  let src = `${baseURL}/${filename}`;
  // time bookmark
  if (startAtTime) src += `#t=${startAtTime}`;
  // to avoid random factor when editing (spaming the server requesting media)
  if (player.editMode) src += `?r=${random}`;

  if (reloading || !isMounted) return null;

  return (
    <div
      className={[
        styles.VideoHtml,
        disableSeek && !getTracking(resourceId)?.completed
          ? styles.VideoHtml__hideSeek
          : '',
      ].join(' ')}
      style={{ backgroundColor: mediaBackground }}
    >
      <video
        ref={videoRef}
        controls
        autoPlay
        controlsList="nodownload"
        // crossOrigin is required to load closed caption file
        // Source: https://stackoverflow.com/questions/55645393/issue-with-loading-vtt-from-cross-domain
        crossOrigin="anonymous"
        onEnded={(e) => {
          onPageCompleted(() => startBlinkNext());
          // reset state in order to allow for rewind
          supposedCurrentTime.current = 0;
          // reset tracking for the next visit return to the begining of the video
          patchTracking(resourceId, { time: 0 });
        }}
        onContextMenu={(e) => e.preventDefault()}
        onTimeUpdate={(e) => {
          if (videoRef.current) {
            if (!videoRef.current?.seeking) {
              supposedCurrentTime.current = videoRef.current.currentTime;
            }

            setLastTimeAt((prevTime) => {
              if (!videoRef.current) return prevTime;

              const _lastTime = handleSetLastTimeAt(
                prevTime.time,
                videoRef.current.currentTime
              );

              return { resourceId, time: _lastTime };
            });
          }
        }}
        onSeeking={(e) => {
          const pageTracking = getTracking(resourceId);
          const pageCompleted = pageTracking?.completed;

          // code below is only valid if disableSeek is enabled
          if (!disableSeek || pageCompleted) return;

          // guard agains infinite recursion:
          // user seeks, seeking is fired, currentTime is modified, seeking is fired, current time is modified, ....
          const delta =
            (videoRef?.current?.currentTime || 0) -
            (supposedCurrentTime?.current || 0);

          // only allows do seek backwards
          if (
            delta > 0.01 &&
            videoRef &&
            videoRef.current &&
            supposedCurrentTime.current
          ) {
            // console.log("Seeking is disabled");
            videoRef.current.currentTime = supposedCurrentTime.current;
          }
        }}
      >
        <source
          src={src}
          type={`video/${extname(filename).replace('.', '')}`}
        />
        {closedCaption?.name ? (
          <track
            src={`${baseURL}/${closedCaption?.name}`}
            // label="English"
            kind="captions"
            // srcLang="en-us"
            default
          />
        ) : null}
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

export const handleSetLastTimeAt = (prevTime: number, currentTime: number) => {
  const setEveryNSeconds = 10;

  if (
    Math.abs(prevTime - Math.floor(currentTime)) > setEveryNSeconds &&
    Math.floor(currentTime) > 0
  ) {
    return Math.floor(currentTime);
  }

  return prevTime;
};

export default VideoHtml;
